// frontend/packages/business-console/src/components/build-interview/MobileStepBar.jsx
//
// Sticky top bar for the build interview on phones (Website Builder Redesign,
// Phase 1): a sparkle tile + title + "N / 10", a thin gradient progress bar, and
// a horizontally scrolling row of step pills (done = gradient check, current =
// labelled blue pill, upcoming = empty circle). Completed pills jump back to
// that step. Shown only on small screens; the journey rail replaces it on
// desktop. Colors via useTokens.

import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import useTokens from '../primitives/useTokens';
import { SparkleTile } from './InterviewShell';

export default function MobileStepBar({
  steps = [],
  activeId = null,
  percent = 0,
  doneCount = 0,
  totalSteps = 10,
  busy = false,
  completed = false,
  onSelect,
}) {
  const t = useTokens();
  const pct = Math.max(0, Math.min(100, Number(percent) || 0));
  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        background: t.appBg,
        borderBottom: `1px solid ${t.cardBorder}`,
        px: 1.5,
        pt: 1.25,
        pb: 1,
        mx: -2, // bleed to the page edges (page has px: 2)
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <SparkleTile size={22} radius={7} />
        <Typography sx={{ fontSize: 14, fontWeight: 900, color: t.text, flex: 1 }}>
          Build your website
        </Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: t.muted }}>
          {doneCount} / {totalSteps}
        </Typography>
      </Box>

      {/* thin gradient progress */}
      <Box sx={{ height: 4, borderRadius: 3, background: t.grayBorder, mb: 1 }}>
        <Box
          sx={{
            height: '100%',
            width: `${pct}%`,
            borderRadius: 3,
            background: t.gradient,
            transition: 'width .35s cubic-bezier(.2,.8,.2,1)',
          }}
        />
      </Box>

      {/* step pills */}
      <Box
        sx={{
          display: 'flex',
          gap: 0.75,
          overflowX: 'auto',
          pb: 0.5,
          // Show a thin scrollbar as a horizontal-scroll affordance instead of
          // hiding it entirely (WS-F A4).
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: t.hairline || 'rgba(0,0,0,0.18)',
            borderRadius: 2,
          },
          scrollbarWidth: 'thin',
          scrollbarColor: `${t.hairline || 'rgba(0,0,0,0.18)'} transparent`,
        }}
      >
        {steps.map((s) => {
          const isDone = !!s.done;
          const isActive = s.id === activeId;
          const clickable = isDone && !completed && !busy;
          if (isActive && !isDone) {
            return (
              <Box
                key={s.id}
                sx={{
                  flexShrink: 0,
                  fontSize: 11.5,
                  fontWeight: 800,
                  color: t.blue,
                  background: t.blueBg,
                  border: `1px solid ${t.blueBorder}`,
                  borderRadius: '999px',
                  px: 1.25,
                  py: 0.5,
                  whiteSpace: 'nowrap',
                }}
              >
                {s.order}. {s.title}
              </Box>
            );
          }
          const onSelectStep =
            clickable && onSelect ? () => onSelect(s.id) : undefined;
          return (
            <Box
              key={s.id}
              onClick={onSelectStep}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              aria-label={clickable ? `Edit step ${s.order}, ${s.title}` : undefined}
              onKeyDown={
                clickable
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectStep();
                      }
                    }
                  : undefined
              }
              sx={{
                flexShrink: 0,
                width: 26,
                height: 26,
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: clickable ? 'pointer' : 'default',
                background: isDone ? t.gradient : t.card,
                border: isDone ? 'none' : `1px solid ${t.grayBorder}`,
              }}
            >
              {isDone ? (
                <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />
              ) : (
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: t.faint }}>
                  {s.order}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
